import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';
import { useAuth } from '../auth/AuthContext';
import { User, Lock, CreditCard, Eye, EyeOff, Check, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';

export default function ProfilePage() {
  const { t, glassCard, glassInput, iri } = useTheme();
  const { user, token } = useAuth();

  const [name, setName] = useState(user?.display_name || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const API = process.env.REACT_APP_API_URL || '/api';

  const saveName = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/auth/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ display_name: name }),
      }).then(r => r.json());
      setMsg(res.success ? { type: 'success', text: 'Name updated!' } : { type: 'error', text: res.message });
    } catch { setMsg({ type: 'error', text: 'Failed to save' }); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) { setMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    if (newPw.length < 8) { setMsg({ type: 'error', text: 'Min. 8 characters' }); return; }
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/auth/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      }).then(r => r.json());
      if (res.success) { setMsg({ type: 'success', text: 'Password changed!' }); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }
      else setMsg({ type: 'error', text: res.message });
    } catch { setMsg({ type: 'error', text: 'Failed to save' }); }
    setSaving(false);
  };

  const section = { background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 14, padding: 24 };
  const sectionTitle = { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textMuted, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 };
  const btnPrimary = { padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: iri, color: '#fff', transition: 'opacity 0.2s', boxShadow: '0 14px 30px rgba(111,168,255,0.18)' };

  const sub = user?.subscription;

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 24px 80px', position: 'relative', zIndex: 1 }}>
        <div className="card-enter" style={{ ...glassCard, padding: 32 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 28 }}>Profile</h1>

          {msg && (
            <div style={{
              background: msg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(244,63,94,0.1)',
              border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)'}`,
              borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13,
              color: msg.type === 'success' ? t.positive : t.negative,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {msg.type === 'success' && <Check size={14} />} {msg.text}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Account Info */}
            <div style={section}>
              <div style={sectionTitle}><User size={13} /> Account</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Email</label>
                  <div style={{ ...glassInput, opacity: 0.6, cursor: 'not-allowed' }}>{user?.email}</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Display Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} style={glassInput} />
                </div>
                <button onClick={saveName} disabled={saving} style={btnPrimary}>Save Name</button>
              </div>
            </div>

            {/* Subscription */}
            <div style={section}>
              <div style={sectionTitle}><CreditCard size={13} /> Subscription</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{
                      borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
                      background: sub?.tier === 'max' ? 'rgba(167,139,250,0.15)' : 'rgba(16,185,129,0.12)',
                      color: sub?.tier === 'max' ? '#8fb7ff' : '#7df9c6',
                      border: `1px solid ${sub?.tier === 'max' ? 'rgba(167,139,250,0.3)' : 'rgba(16,185,129,0.25)'}`,
                      textTransform: 'uppercase',
                    }}>{sub?.tier || 'Free'}</span>
                    <span style={{ fontSize: 12, color: t.positive, fontWeight: 600 }}>{sub?.status || 'inactive'}</span>
                  </div>
                  <p style={{ fontSize: 13, color: t.textMuted }}>
                    {sub?.tier === 'max' ? '$79/mo — Unlimited channels, priority execution' : '$29/mo — 1 channel, standard execution'}
                  </p>
                </div>
                {sub?.tier !== 'max' && (
                  <Link to="/pricing" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: t.primaryText }}>
                    Upgrade <ArrowRight size={13} />
                  </Link>
                )}
              </div>
            </div>

            {/* Change Password */}
            <div style={section}>
              <div style={sectionTitle}><Lock size={13} /> Change Password</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} style={{ ...glassInput, paddingRight: 40 }} placeholder="Enter current password" />
                    <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: t.textFaint, cursor: 'pointer' }}>
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>New Password</label>
                    <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={glassInput} placeholder="Min. 8 characters" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: t.textDim, display: 'block', marginBottom: 6 }}>Confirm</label>
                    <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={glassInput} placeholder="Confirm password" />
                  </div>
                </div>
                <button onClick={changePassword} disabled={saving || !currentPw || !newPw} style={{ ...btnPrimary, opacity: (!currentPw || !newPw) ? 0.5 : 1 }}>Change Password</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
